import React, {
  Dispatch,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useFeatureFlags } from '@context/FeatureFlagsContext'

import { getTickmillCompanyByHostname } from '@utils/companyName.utils'
import { dispatchEntitySelected } from '@utils/cookie.utils'

import { Locale, OverrideLocaleForZHLanguage, storeLocaleClientSide } from '@global/locale/Locale'
import {
  AllThemes,
  Theme,
  applyThemeClientSide,
  defaultTheme,
  detectThemeClientSide,
  storeThemeClientSide,
} from '@global/theme/Theme'
import { useGuardedContext } from '@global/useGuardedContext'

import { useLanguageTracker } from '@analytics/amplitude/language'

import { useAccountReadContext, useAccountWriteContext } from './AccountContextContext'

export interface SessionSettings {
  theme: Theme
  locale: Locale
}

type SessionSettingsContextType = [SessionSettings, Dispatch<Partial<SessionSettings>>] | undefined

const SessionSettingsContext = createContext<SessionSettingsContextType>(undefined)
SessionSettingsContext.displayName = 'SessionSettingsContext'

export const getEffectiveTheme = (theme: Theme): Exclude<Theme, 'auto'> => {
  if (theme !== 'auto') return theme
  const dataTheme = document.body.getAttribute('data-theme') as Exclude<Theme, 'auto'>
  if (AllThemes.includes(dataTheme)) return dataTheme
  return defaultTheme
}

const normalizeLang = (val?: string | null): string => {
  if (!val) return ''
  const tag = val.trim().replace(/_/g, '-')
  try {
    const [canon] = Intl.getCanonicalLocales(tag)
    return (canon ?? tag).toLowerCase()
  } catch {
    return tag.toLowerCase()
  }
}

const toHtmlLangTag = (loc: Locale | OverrideLocaleForZHLanguage | string): string => {
  const v = String(loc).toLowerCase()
  if (v === 'cimode') return 'en'
  if (v === 'cn' || v === 'zh') return 'zh-CN'
  if (v === 'kr' || v === 'ko') return 'ko-KR'
  return String(loc)
}

const isRtlTag = (tag: string): boolean => /^(ar|fa|he|ur)\b/i.test(tag)

const applyHtmlLangAndDir = (loc: Locale | OverrideLocaleForZHLanguage | string) => {
  if (typeof document === 'undefined') return
  const tag = normalizeLang(toHtmlLangTag(loc))
  const dir = isRtlTag(tag) ? 'rtl' : 'ltr'
  const html = document.documentElement
  if (html.getAttribute('lang') !== tag) html.setAttribute('lang', tag)
  if (html.getAttribute('dir') !== dir) html.setAttribute('dir', dir)
  const root = document.getElementById('root')
  if (root && root.getAttribute('dir') !== dir) root.setAttribute('dir', dir)
  document.body.classList.toggle('localeArabic', /^ar\b/i.test(tag))
}

const readDomLang = (): string => {
  if (typeof document === 'undefined') return ''
  const root = document.documentElement
  const raw = root.getAttribute('lang') || (root as any).lang || root.getAttribute('xml:lang') || ''
  let lang = normalizeLang(raw)
  if (!lang && typeof navigator !== 'undefined') {
    lang = normalizeLang(navigator.languages?.[0] || navigator.language)
  }
  return lang
}

const getUrlLocale = (): string => {
  if (typeof window === 'undefined') return ''
  return window.location.pathname.split('/')[1] || ''
}

export const useArabicSessionLanguage = (): boolean => {
  const domLang = readDomLang()
  const isArabicFromDom = domLang === 'ar' || domLang.startsWith('ar-') || domLang.startsWith('ar_')

  if (!isArabicFromDom) {
    const urlLocale = getUrlLocale()
    return urlLocale === 'ar'
  }

  return isArabicFromDom
}

export const useChineseSessionLanguage = (): boolean => {
  const domLang = readDomLang()
  const isChineseFromDom =
    domLang === 'zh' || domLang.startsWith('zh-') || domLang.startsWith('zh_')

  if (!isChineseFromDom) {
    const urlLocale = getUrlLocale()
    return urlLocale === 'zh'
  }

  return isChineseFromDom
}

export const useIsRTL = (): boolean => {
  if (typeof document === 'undefined') return false
  return document.documentElement.dir === 'rtl'
}

export function useSessionSettings(): [SessionSettings, Dispatch<Partial<SessionSettings>>] {
  return useGuardedContext(SessionSettingsContext)
}

export function useSessionTheme(): Theme {
  const [settings] = useSessionSettings()
  return settings.theme
}

export function useSessionLanguage(): Locale {
  const [settings] = useSessionSettings()
  return settings.locale
}

export function useOptionalSessionLanguage(): Locale | undefined {
  const ctx = useContext(SessionSettingsContext)
  const settings = ctx ? (ctx[0] as SessionSettings) : undefined
  return settings?.locale
}

export function overrideTraditionalChineseLocale(lang: Locale): Locale {
  if (lang === 'hk' || lang === 'tw') return 'zh'
  return lang
}

export function overrideForZHToCNLanguage(
  lang: Locale | OverrideLocaleForZHLanguage
): OverrideLocaleForZHLanguage {
  if (lang === 'zh' || lang === 'cn') return 'cn'
  return lang
}

export function overrideKoToKrLanguage(lang: Locale) {
  if (lang === 'ko') return 'kr'
  return lang
}

export function isOverrideLocaleForENorTR(lang: Locale): boolean {
  return lang === 'en' || lang === 'tr'
}

interface Props {
  initialSettings: SessionSettings
  children: ReactNode
  applyTheme?: boolean
}

export const SessionSettingsContextProvider: React.FC<Props> = ({
  initialSettings,
  children,
  applyTheme = false,
}) => {
  const { i18n } = useTranslation()
  const { refreshAccount } = useAccountWriteContext()
  const { account } = useAccountReadContext()
  const { darkMode } = useFeatureFlags()
  const [settings, _setSettings] = useState(initialSettings)

  const caLanguageChange = useLanguageTracker()

  useEffect(() => {
    const detectedTheme = detectThemeClientSide()
    applyThemeClientSide(darkMode ? detectedTheme : 'light', applyTheme)
    _setSettings((s) => ({ ...s, theme: detectedTheme }))
  }, [applyTheme, darkMode])

  useEffect(() => {
    applyHtmlLangAndDir(settings.locale)
  }, [settings.locale])

  useEffect(() => {
    const onLang = (lng: string) => applyHtmlLangAndDir(lng)
    i18n.on('languageChanged', onLang)
    return () => i18n.off('languageChanged', onLang)
  }, [i18n])

  const setSettings = useCallback(
    (newSettings: Partial<SessionSettings>) => {
      const { locale, theme } = newSettings

      if (locale && locale !== settings.locale) {
        caLanguageChange({ newLanguage: locale, previousLanguage: settings.locale })
        storeLocaleClientSide(i18n, locale)
        applyHtmlLangAndDir(locale)

        if (account) {
          refreshAccount(locale)
        }
        dispatchEntitySelected(
          account?.tickmillCompany.id || getTickmillCompanyByHostname(),
          locale,
          account?.emails[0]?.address || '',
          account?.firstname
        )
      }

      if (theme && theme !== settings.theme) {
        storeThemeClientSide(darkMode ? theme : 'light', applyTheme)
      }

      _setSettings({ ...settings, ...newSettings })
    },
    [settings, i18n, account, refreshAccount, darkMode, applyTheme, caLanguageChange]
  )

  return (
    <SessionSettingsContext.Provider value={[settings, setSettings]}>
      {children}
    </SessionSettingsContext.Provider>
  )
}
